import { EQUIPMENT_KEY_TO_ID } from "../bookingCatalog";

const normalizeGuid = (value) => (typeof value === "string" ? value.toLowerCase() : value);

const EQUIPMENT_ID_TO_KEY = Object.entries(EQUIPMENT_KEY_TO_ID).reduce((acc, [key, id]) => {
  if (id) {
    acc[normalizeGuid(id)] = key;
  }
  return acc;
}, {});

export const EMPTY_PRICING_MAPS = {
  basePrices: {},
  halfHourPrices: {},
  dayPrices: {},
  weeklyPrices: {},
  tripPrices: {},
};

export const parsePriceValue = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) && value >= 0 ? value : null;
  }
  if (typeof value !== "string") {
    return null;
  }
  const cleaned = value.replace(/[^0-9.-]/g, "");
  if (!cleaned) {
    return null;
  }
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

const resolveEquipmentKeyFromName = (rawName) => {
  if (!rawName) return null;
  const name = String(rawName).toLowerCase();
  const isBig = name.includes("big") || name.includes("tandem") || name.includes("xl");

  if (name.includes("utilities")) return "kit";

  if (name.includes("neon")) return "neon_board";
  if (name.includes("kit")) return "kit";
  if (name.includes("sup")) return isBig ? "big_sup" : "sup";
  if (name.includes("kayak")) return isBig ? "big_kayak" : "kayak";

  return null;
};

const normalizeNameForKey = (value) =>
  typeof value === "string"
    ? value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim()
    : "";

const resolveEquipmentKey = (row) => {
  if (!row || typeof row !== "object") return null;

  const id = row.id ?? row.equipmentTypeId;
  if (id) {
    const mapped = EQUIPMENT_ID_TO_KEY[normalizeGuid(id)];
    if (mapped) return mapped;
  }

  const typeName = row.equipmentTypeName ?? row.name;
  return resolveEquipmentKeyFromName(typeName);
};

const rowQualityScore = (row) => {
  if (!row) return 0;
  let score = 0;
  ["firstHour", "nextHalfHour", "fullDay", "weekly", "trip"].forEach((field) => {
    if (parsePriceValue(row[field]) !== null) {
      score += 1;
    }
  });
  return score;
};

const getNormalizedRowName = (row) =>
  normalizeNameForKey(row?.name ?? row?.equipmentTypeName ?? "");

const nameMatchesKey = (row, key) =>
  getNormalizedRowName(row) === normalizeNameForKey(key.replace(/_/g, " "));

const isSeedRow = (row) => getNormalizedRowName(row).includes("seed");

export const mapPricingRowsByKey = (pricingSettings) => {
  const rows = Array.isArray(pricingSettings?.basePricing?.rows)
    ? pricingSettings.basePricing.rows
    : [];
  const mapped = rows.reduce((acc, row) => {
    const key = resolveEquipmentKey(row);
    if (!key) return acc;
    const existing = acc[key];
    const preferredId = EQUIPMENT_KEY_TO_ID[key];
    const candidateScore = rowQualityScore(row);
    const existingScore = existing ? rowQualityScore(existing) : -1;
    const candidatePriority =
      (preferredId &&
      normalizeGuid(row.id ?? row.equipmentTypeId) === normalizeGuid(preferredId)
        ? 3
        : 0) + (nameMatchesKey(row, key) && !isSeedRow(row) ? 2 : 0);
    const existingPriority =
      (preferredId &&
      normalizeGuid(existing?.id ?? existing?.equipmentTypeId) ===
        normalizeGuid(preferredId)
        ? 3
        : 0) + (nameMatchesKey(existing, key) && !isSeedRow(existing) ? 2 : 0);

    const shouldReplace =
      !existing ||
      candidatePriority > existingPriority ||
      (candidatePriority === existingPriority &&
        (candidateScore > existingScore ||
          (candidateScore === existingScore &&
            !isSeedRow(row) &&
            isSeedRow(existing))));

    if (shouldReplace) {
      acc[key] = row;
    }
    return acc;
  }, {});
  return mapped;
};

export const buildPricingMaps = (pricingSettings, fallback = EMPTY_PRICING_MAPS) => {
  const basePrices = { ...(fallback?.basePrices || {}) };
  const halfHourPrices = { ...(fallback?.halfHourPrices || {}) };
  const dayPrices = { ...(fallback?.dayPrices || {}) };
  const weeklyPrices = { ...(fallback?.weeklyPrices || {}) };
  const tripPrices = { ...(fallback?.tripPrices || {}) };

  const rows = pricingSettings?.basePricing?.rows;
  if (!Array.isArray(rows)) {
    return { basePrices, halfHourPrices, dayPrices, weeklyPrices, tripPrices };
  }

  rows.forEach((row) => {
    const key = resolveEquipmentKey(row);
    if (!key) return;

    const firstHour = parsePriceValue(row.firstHour);
    if (firstHour !== null) {
      basePrices[key] = firstHour;
    }

    const nextHalfHour = parsePriceValue(row.nextHalfHour);
    if (nextHalfHour !== null) {
      halfHourPrices[key] = nextHalfHour;
    }

    const fullDay = parsePriceValue(row.fullDay);
    if (fullDay !== null) {
      dayPrices[key] = fullDay;
    }

    const weekly = parsePriceValue(row.weekly);
    if (weekly !== null) {
      weeklyPrices[key] = weekly;
    }

    const trip = parsePriceValue(row.trip);
    if (trip !== null) {
      tripPrices[key] = trip;
    }

  });

  return { basePrices, halfHourPrices, dayPrices, weeklyPrices, tripPrices };
};

export default buildPricingMaps;
